import { Button, MagellanPopup } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { useFormikContext } from 'formik';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiBroom,
  PiCalendarMinus,
  PiCalendarPlus,
  PiPlus,
  PiScroll,
} from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { ICreateContractFromTemplateForm } from 'services/contract/types';
import { ILabelForm } from 'services/labels/types';
import { useGetContractTemplateLightList } from 'services/templates/contract-template';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDateToInput } from 'utils/global/date.utils';

export const initialPopupContractFromTemplateValues: ICreateContractFromTemplateForm =
  {
    contractTemplateId: '',
    endDate: formatDateToInput(new Date()),
    startDate: formatDateToInput(new Date()),
    withEndDate: false,
  };

interface IPopupAddContractFromTemplateProps {
  trigger: JSX.Element;
  onAddClick: (
    contract: ICreateContractFromTemplateForm,
    actions: PopupActions
  ) => void;
}

const PopupAddContractFromTemplate = ({
  trigger,
  onAddClick,
}: IPopupAddContractFromTemplateProps) => {
  const { t } = useTranslation();

  const popupRef = useRef<PopupActions>(null);

  const { setFieldValue, values } = useFormikContext<ILabelForm>();

  const getContractTemplatesLightList = useGetContractTemplateLightList();

  const templateOptions =
    getContractTemplatesLightList.data?.map(contract => ({
      label: contract.title ?? '',
      value: contract.id,
    })) ?? [];

  const clearPopupValues = () => {
    setFieldValue(
      'contractFromTemplate',
      initialPopupContractFromTemplateValues
    );
  };

  const disabledOnAdd = !values.contractFromTemplate?.contractTemplateId;

  return (
    <Popup ref={popupRef} modal nested trigger={trigger}>
      <MagellanPopup>
        <MagellanPopup.Header className='justify-center w-80 mb-5'>
          <PiScroll /> {t('label.contract.add-with-template')}
        </MagellanPopup.Header>
        <FormInput.Select
          name='contractFromTemplate.contractTemplateId'
          label={t('input.label.contract-template')}
          options={templateOptions}
          displayMode={false}
          autoFocus
        />
        <FormInput.Text
          name='contractFromTemplate.startDate'
          type='date'
          label={t('input.label.startDate')}
          displayMode={false}
        />
        {values.contractFromTemplate?.withEndDate ? (
          <div className='flex gap-2 w-full'>
            <FormInput.Text
              name='contractFromTemplate.endDate'
              type='date'
              label={t('input.label.endDate')}
              min={values.contractFromTemplate.startDate}
              displayMode={false}
            />
            <Button
              isClear
              onlyIcon
              onClick={() =>
                setFieldValue('contractFromTemplate.withEndDate', false)
              }>
              <Button.Description description={t('button.remove')} />
              <Button.Icon icon={<PiCalendarMinus />} />
            </Button>
          </div>
        ) : (
          <Button
            isClear
            fullWidth
            onClick={() =>
              setFieldValue('contractFromTemplate.withEndDate', true)
            }>
            <Button.Text text={t('input.label.endDate')} />
            <Button.Icon icon={<PiCalendarPlus />} />
          </Button>
        )}

        <MagellanPopup.BottomButtons>
          <Button isClear onlyIcon onClick={clearPopupValues}>
            <Button.Description description={t('button.clear')} />
            <Button.Icon icon={<PiBroom />} />
          </Button>
          <Button
            variant={BUTTON_VARIANT.submit}
            disabled={disabledOnAdd}
            onClick={() =>
              onAddClick &&
              popupRef.current &&
              values.contractFromTemplate &&
              onAddClick(values.contractFromTemplate, popupRef.current)
            }>
            <Button.Text text={t('button.add')} />
            <Button.Icon icon={<PiPlus />} />
          </Button>
        </MagellanPopup.BottomButtons>
      </MagellanPopup>
    </Popup>
  );
};

export default PopupAddContractFromTemplate;
